import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { ServiceNav } from "@/components/ServiceNav";
import { ServiceLayout } from "@/components/ServiceLayout";
import NutritionCounselling from "./services/NutritionCounselling";
import GutHealth from "./services/GutHealth";
import WeightManagement from "./services/WeightManagement";
import SportsNutrition from "./services/SportsNutrition";
import ChronicDiseases from "./services/ChronicDiseases";
import LifecycleNutrition from "./services/LifecycleNutrition";

const serviceMeta: Record<string, { title: string; description: string; path: string }> = {
  "/services": {
    title: "Dietitian Services in Johannesburg | Functional Foodie",
    description: "Registered dietitian services in Johannesburg: nutrition counselling, gut health, weight management, sports, chronic disease and lifecycle nutrition.",
    path: "/services",
  },
  "/services/nutrition-counselling": {
    title: "Nutrition Counselling Johannesburg | Functional Foodie",
    description: "One-on-one nutrition counselling with a registered dietitian in Johannesburg. Personalised plans, evidence-based advice and ongoing support.",
    path: "/services/nutrition-counselling",
  },
  "/services/gut-health": {
    title: "Gut Health & IBS Dietitian Johannesburg | Functional Foodie",
    description: "Dietitian care for IBS, IBD, reflux and bloating in Johannesburg. Low FODMAP and evidence-based gut health plans tailored to you.",
    path: "/services/gut-health",
  },
  "/services/weight-management": {
    title: "Weight Loss Dietitian Johannesburg | Functional Foodie",
    description: "Sustainable weight loss with a registered dietitian in Johannesburg. Personalised meal plans, body composition tracking and lifestyle support.",
    path: "/services/weight-management",
  },
  "/services/sports-nutrition": {
    title: "Sports Nutrition Dietitian Johannesburg | Functional Foodie",
    description: "Sports nutrition plans for recreational athletes in Johannesburg. Fuelling, recovery, hydration and supplement guidance from a dietitian.",
    path: "/services/sports-nutrition",
  },
  "/services/chronic-diseases": {
    title: "Chronic Disease Dietitian Johannesburg | Functional Foodie",
    description: "Dietitian support for diabetes, cholesterol, PCOS, hypertension and more in Johannesburg. Evidence-based plans alongside your medical team.",
    path: "/services/chronic-diseases",
  },
  "/services/lifecycle-nutrition": {
    title: "Pregnancy, Infant & Elderly Nutrition Johannesburg | Functional Foodie",
    description: "Dietitian guidance for pregnancy, infants, children and older adults in Johannesburg. Practical, evidence-based nutrition for every life stage.",
    path: "/services/lifecycle-nutrition",
  },
};

const Services = () => {
  const { pathname } = useLocation();
  const meta = serviceMeta[pathname] ?? serviceMeta["/services"];
  const url = `https://functionalfoodie.co.za${meta.path}`;
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "Functional Foodie Registered Dietitians",
    url: "https://functionalfoodie.co.za/services",
    medicalSpecialty: "Dietetics",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Dietitian Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Nutrition Counselling", url: "https://functionalfoodie.co.za/services/nutrition-counselling" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Gut Health & IBS", url: "https://functionalfoodie.co.za/services/gut-health" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Weight Management", url: "https://functionalfoodie.co.za/services/weight-management" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Sports Nutrition", url: "https://functionalfoodie.co.za/services/sports-nutrition" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Chronic Disease Nutrition", url: "https://functionalfoodie.co.za/services/chronic-diseases" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Lifecycle Nutrition", url: "https://functionalfoodie.co.za/services/lifecycle-nutrition" } },
      ],
    },
  };
  return <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>
      <div className="min-h-screen pt-40 pb-16">
        <div className="container mx-auto px-4 mb-12">
          <h1 className="text-4xl font-bold text-center text-primary mb-8 my-px">Our Services</h1>
          <p className="text-lg text-center text-gray-600 max-w-3xl mx-auto">
            At Functional Foodie, we offer a comprehensive range of nutrition services to support your health and wellness goals. Browse through our services below or click on a specific service to learn more.
          </p>
        </div>

        <ServiceNav />
        
        <div className="space-y-16 mt-8">
          <ServiceLayout serviceId="nutrition-counselling">
            <NutritionCounselling />
          </ServiceLayout>
          
          <ServiceLayout serviceId="gut-health">
            <GutHealth />
          </ServiceLayout>
          
          <ServiceLayout serviceId="weight-management">
            <WeightManagement />
          </ServiceLayout>
          
          <ServiceLayout serviceId="sports-nutrition">
            <SportsNutrition />
          </ServiceLayout>
          
          <ServiceLayout serviceId="chronic-diseases">
            <ChronicDiseases />
          </ServiceLayout>
          
          <ServiceLayout serviceId="lifecycle-nutrition">
            <LifecycleNutrition />
          </ServiceLayout>
        </div>
      </div>
    </>;
};
export default Services;